<template>
  <div class="tab-lesson-line-list-component custom-scrollbar pt-4">
    <div class="line-list-items flex-fill custom-scrollbar">
      <router-link
        v-for="(item, i) in items"
        :key="`item-${item.id}`"
        class="list-item text-decoration-none"
        :to="getRoute(item)"
        :class="{ 'is-current': isCurrent(item) }"
      >
        <div class="d-flex flex-column align-items-center mr-3">
          <div
            class="circle"
            :class="{ fill: isCurrent(item), finished: isFinished(item) }"
          >
            <i
              v-if="isFinished(item) && !isCurrent(item)"
              class="icon-check border-0"
            ></i>
            <i
              v-else-if="isCurrent(item)"
              class="fa-solid fa-play current-icon"
            ></i>
          </div>
          <div v-if="i < items.length - 1" class="line"></div>
        </div>
        <div class="item-content">
          <div class="item-header">
            <i v-if="isQuiz(item)" class="fa fa-file-pen quiz-icon"></i>
            <p class="item-title" :class="{ current: isCurrent(item) }">
              {{ getTitle(item) }}
            </p>
          </div>
          <div class="item-meta">
            <span v-if="getDuration(item)" class="duration">
              <i class="fa-regular fa-clock"></i>
              {{ getDuration(item) }}
            </span>
            <span
              v-if="getStoppedAt(item) && !isFinished(item)"
              class="stopped-at"
            >
              <i class="fa-solid fa-bookmark"></i>
              Parou em {{ formatTime(getStoppedAt(item)) }}
            </span>
            <span v-if="isFinished(item)" class="completed-badge">
              <i class="fa-solid fa-check"></i>
              Concluída
            </span>
          </div>
        </div>
      </router-link>
    </div>
  </div>
</template>
<script>
import get from "lodash/get";
import { duration_seconds_to_humanized } from "@/functions/helpers";

export default {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    current: {
      type: Object,
      default: () => {}
    }
  },
  methods: {
    isQuiz(item) {
      return [
        "approval_average",
        "time_limit_minutes",
        "number_of_attempts"
      ].reduce((acc, cur) => acc && Object.keys(item).includes(cur), true);
    },
    getTitle(item) {
      if (this.isQuiz(item)) return item.title;
      return item.name;
    },
    isCurrent(item) {
      return this.current.id === item.id;
    },
    isFinished(item) {
      return get(item, "progress.finished_at", false);
    },
    getDuration(item) {
      const duration = get(item, "duration", 0);
      if (!duration || duration === 0) return null;
      return duration_seconds_to_humanized(duration * 60);
    },
    getStoppedAt(item) {
      return get(item, "progress.stopped_at", 0);
    },
    formatTime(seconds) {
      if (!seconds) return "";
      return duration_seconds_to_humanized(seconds);
    },
    getRoute(item) {
      if (this.isQuiz(item)) {
        return {
          name: "lesson-quiz",
          params: { id: item.module, quiz: item.code }
        };
      }
      return {
        name: "lesson",
        params: { id: item.module.id, lesson: item.id }
      };
    }
  }
};
</script>
<style lang="scss" scoped>
.tab-lesson-line-list-component {
  display: flex;
  flex-direction: column;
  height: -webkit-fill-available;

  .line-list-items {
    overflow-y: scroll;
    height: 400px;
    @media screen and (max-width: 991px) {
      height: 300px;
    }
  }

  .list-item {
    position: relative;
    display: flex;
    font-size: 14px;
    min-height: 70px;
    padding: 8px;
    margin: 0 -8px;
    border-radius: 8px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(62, 86, 172, 0.08);
    }

    &.is-current {
      background-color: rgba(62, 86, 172, 0.12);
      border-left: 3px solid #ab85e5;
      margin-left: -11px;
      padding-left: 11px;
    }

    .circle {
      flex-shrink: 0;
      height: 28px;
      width: 28px;
      border-radius: 50%;
      border: 2px solid #3e56ac;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      &.fill {
        background: linear-gradient(135deg, #3e56ac 0%, #ab85e5 100%);
        border-color: #ab85e5;
        box-shadow: 0 0 10px rgba(171, 133, 229, 0.4);

        .current-icon {
          color: white;
          font-size: 10px;
        }
      }

      &.finished {
        background-color: #62ad3e;
        border-color: #62ad3e;
        position: relative;

        .icon-check {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #fff;
          font-size: 18px;
        }
      }
    }

    i {
      line-height: 1.5;
    }

    .line {
      width: 2px;
      height: 100%;
      background-color: rgba(62, 86, 172, 0.3);
    }

    .item-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      min-width: 0;
    }

    .item-header {
      display: flex;
      align-items: flex-start;
      gap: 6px;

      .quiz-icon {
        color: #ab85e5;
        font-size: 12px;
        margin-top: 2px;
        flex-shrink: 0;
      }
    }

    .item-title {
      font-size: 13px;
      font-weight: 400;
      line-height: 1.4;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;

      &.current {
        font-weight: 600;
        color: #ab85e5;
      }
    }

    .item-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 4px;

      span {
        font-size: 11px;
        display: flex;
        align-items: center;
        gap: 4px;

        i {
          font-size: 10px;
        }
      }

      .duration {
        color: rgba(255, 255, 255, 0.5);
      }

      .stopped-at {
        color: #e4af37;
        background: rgba(228, 175, 55, 0.1);
        padding: 2px 6px;
        border-radius: 4px;

        i {
          color: #e4af37;
        }
      }

      .completed-badge {
        color: #62ad3e;

        i {
          color: #62ad3e;
        }
      }
    }
  }
}
</style>
